import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { IoShareSocial } from 'react-icons/io5';
import { FaSteam, FaInfoCircle } from 'react-icons/fa';
import WebApp from "@twa-dev/sdk";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSoundContext } from '../contexts/SoundContext';
import { apiService } from '../services/api';
import { useTranslation, Trans } from 'react-i18next';
import useUserStore from '../stores/userStore';
import useInventoryStore from '../stores/inventoryStore';
import { useTheme } from 'styled-components';
import CurrencyIcon from './CurrencyIcon';
import useScrollToTop from '../hooks/useScrollToTop';

const API_URL = process.env.REACT_APP_API_URL;

const Container = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.background};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  ${'' /* padding-top: 50px; */}

  /* Скрываем скроллбар, но оставляем функциональность */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Content = styled.div`
  flex: 1;
  width: 100%;
  padding: 20px;
  padding-bottom: calc(env(safe-area-inset-bottom) + 180px);
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: ${props => props.theme.background};
`;

const ImageSection = styled.div`
  width: 100%;
  height: 30vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3), transparent);
  padding: 20px;
  margin-top: env(safe-area-inset-top, 20px);
`;

const WeaponImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 0 20px;
  margin-bottom: 20px;
`;

const ActionButton = styled.button`
  background: ${props => props.theme.card.background};
  border: none;
  border-radius: 12px;
  padding: 16px;
  color: ${props => props.theme.text.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 20px;
    opacity: 0.8;
  }
`;

const ActionText = styled.span`
  font-size: 13px;
  opacity: 0.8;
`;

const InfoTable = styled.div`
  ${'' /* margin: 20px; */}
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px ${props => props.theme.type === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(0, 0, 0, 0.2)'};
`;

const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  padding: 15px;
  border-bottom: 1px solid ${props => props.theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  color: ${props => props.theme.text?.secondary || '#aaa'};
  font-size: 14px;
`;

const InfoValue = styled.div`
  color: ${props => props.theme.text?.primary || '#fff'};
  font-size: 14px;
`;

const SellButton = styled.button`
  width: 100%;
  background: ${props => props.theme.primary};
  border: none;
  border-radius: 12px;
  padding: 16px;
  color: white;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WeaponTitle = styled.div`
  text-align: center;
  padding: 20px;

  h1 {
    margin: 0;
    font-size: 24px;
    color: ${props => props.theme.text.primary};
  }

  p {
    margin: 5px 0 0;
    color: ${props => props.theme.text?.secondary || '#aaa'};
    font-size: 14px;
  }
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DialogOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
`;

const DialogContent = styled.div`
  position: relative;
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  z-index: 1001;
`;

const DialogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const DialogTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.text.primary};
`;

const DialogText = styled.div`
  color: ${props => props.theme.text.secondary};
  margin-bottom: 24px;
  text-align: center;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const DialogButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(DialogButton)`
  background: rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.text.primary};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ConfirmButton = styled(DialogButton)`
  background: ${props => props.theme.primary};
  color: white;

  &:hover {
    opacity: 0.9;
  }
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  margin-top: 4px;
  font-size: 13px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.text.primary};
  margin: 0 0 16px 0;
  text-align: center;
`;

const SharePreview = styled.div`
  background: rgba(30, 30, 30, 0.98);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  padding: 24px 16px 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
`;

const ShareImage = styled.img`
  width: 220px;
  height: 180px;
  object-fit: contain;
  border-radius: 16px;
  background: #232323;
  margin-bottom: 16px;
`;

const ShareTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  margin-bottom: 4px;
`;

const ShareSubtitle = styled.div`
  font-size: 15px;
  color: #bbb;
  text-align: center;
  margin-bottom: 12px;
`;

const ShareText = styled.div`
  font-size: 16px;
  color: #fff;
  text-align: center;
  margin-bottom: 12px;
`;

const ShareButtonBig = styled.button`
  background: #229ED9;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px 32px;
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-top: 8px;
  box-shadow: 0 2px 8px rgba(34,158,217,0.15);
  transition: background 0.2s;
  &:hover { background: #1b7fa8; }
`;

const BottomContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0));
  width: 100%;
  box-sizing: border-box;
`;

const Price = styled.span`
  color: #0A84FF;
  font-weight: 700;
`;

const PriceInline = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

// Добавляю функцию для парсинга marketHashName
function parseMarketHashName(marketHashName) {
  if (!marketHashName) return { name: '', description: '' };
  const [name, ...rest] = marketHashName.split('|');
  return {
    name: name.trim(),
    description: rest.join('|').trim()
  };
}

// Добавляю компонент для изображения в модалке
const ModalWeaponImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: ${props => props.theme.card.imageBg || '#f5f5f5'};
  object-fit: contain;
  display: block;
`;

// Компонент подсказки для ошибки trade link
const HelpBox = styled.div`
  background: ${({ theme }) => theme.card.background};
  border-radius: 18px;
  padding: 28px 24px 20px 24px;
  margin: 0 0 12px 0;
  color: ${({ theme }) => theme.text.primary};
  font-size: 15px;
  max-width: 420px;
  width: 100%;
  position: relative;
`;

const HelpTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.type === 'light' ? '#1a1a1a' : '#fff'};

  svg {
    font-size: 32px !important;
  }
`;

const HelpStep = styled.div`
  margin-bottom: 7px;
  line-height: 1.6;
`;

const HelpLink = styled.a`
  color: #4b69ff;
  word-break: break-all;
  text-decoration: underline;
  font-family: monospace;
  font-size: 14px;
`;

const CloseHelpButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(34,158,217,0.10);
  transition: background 0.2s;
  &:hover { background: #005ecb; }
`;

const Spinner = styled.div`
  border: 3px solid rgba(255,255,255,0.2);
  border-top: 3px solid #fff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function TradeErrorHelp() {
  const { t, i18n } = useTranslation();
  return (
    <HelpBox>
      <HelpTitle>
        <FaInfoCircle style={{ color: '#4b69ff', fontSize: 22 }} />
        {t('tradeErrorHelp.title')}
      </HelpTitle>
      <HelpStep>{t('tradeErrorHelp.step1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step1_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step1_2')}</HelpStep>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step2_3')}</HelpStep>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step3')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step3_1')}</HelpStep>
      <HelpLink href="https://steamcommunity.com/tradeoffer/new/?partner=XXXXXXX&token=XXXX" target="_blank" rel="noopener noreferrer">
        https://steamcommunity.com/tradeoffer/new/?partner=XXXXXXX&token=XXXX
      </HelpLink>
      <HelpStep style={{ marginTop: 10 }}>{t('tradeErrorHelp.step4')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_1')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_2')}</HelpStep>
      <HelpStep>{t('tradeErrorHelp.step4_3')}</HelpStep>
    </HelpBox>
  );
}

// Функция для капитализации первой буквы
function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const SkinDetails = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { weaponId } = useParams();
  const user = useUserStore(state => state.user);
  const fetchUser = useUserStore(state => state.fetchUser);
  const balance = user?.balance ? parseFloat(user.balance) : 0;
  const [isSelling, setIsSelling] = useState(false);
  const [weapon, setWeapon] = useState(null); // только для предмета из стора
  const [apiWeapon, setApiWeapon] = useState(null); // для предмета из API
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { play, isSoundEnabled, sellSound } = useSoundContext();
  const { t } = useTranslation();
  const [showShare, setShowShare] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const theme = useTheme();
  const primaryTextColor = theme.text?.primary || '#000';
  const [showTradeHelp, setShowTradeHelp] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  // Получаем предметы из стора
  const inventoryItems = useInventoryStore(state => state.items);
  const fetchItems = useInventoryStore(state => state.fetchItems);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    setWeapon(null);
    setApiWeapon(null);

    const fetchWeaponData = async () => {
      try {
        // 1. Сначала ищем предмет в сторе
        const itemFromStore = inventoryItems.find(item => item.id === weaponId);
        if (itemFromStore) {
          if (isMounted) {
            setWeapon(itemFromStore);
            setLoading(false);
          }
          return;
        }
        // 2. Если предмета нет в сторе, делаем запрос к API
        const response = await apiService.getWeaponById(weaponId);
        if (!isMounted) return;
        if (response && response.item) {
          setApiWeapon(response.item);
          setLoading(false);
          return;
        }
        setError('Предмет не найден');
        setLoading(false);
      } catch (error) {
        if (isMounted) {
          setError('Ошибка при загрузке предмета');
          setLoading(false);
        }
      }
    };
    fetchWeaponData();
    return () => { isMounted = false; };
  }, [weaponId, inventoryItems]);

  useEffect(() => {
    if (WebApp.platform !== 'unknown') {
      WebApp.BackButton.show();
      WebApp.BackButton.onClick(() => {
        navigate(-1);
      });
    }
    return () => {
      if (WebApp.platform !== 'unknown') {
        WebApp.BackButton.hide();
        WebApp.BackButton.offClick();
      }
    };
  }, [navigate]);

  // Функция для расчета оставшегося времени
  const calculateTimeLeft = (releaseDate) => {
    if (!releaseDate) return '';

    const now = new Date().getTime();
    const release = new Date(releaseDate).getTime();
    const difference = release - now;

    if (difference <= 0) return '';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };



  // Обновление таймера каждую минуту
  useEffect(() => {
    const currentWeapon = weapon || apiWeapon;
    if (!currentWeapon?.releaseDate) return;

    const updateTimer = () => {
      const timeLeftStr = calculateTimeLeft(currentWeapon.releaseDate);
      setTimeLeft(timeLeftStr);
    };

    updateTimer(); // Первоначальный расчет
    const interval = setInterval(updateTimer, 60000); // Обновление каждую минуту

    return () => clearInterval(interval);
  }, [weapon, apiWeapon]);

  const handleBack = () => {
    navigate('/');
  };

  const handleSellClick = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
    }
    setShowConfirmDialog(true);
  };

  const handleSell = async () => {
    if (!weapon || isSelling) return;
    setIsSelling(true);

    try {
      // Отправляем запрос на создание продажи
      await apiService.createSale(weapon.id);

      // Обновляем инвентарь пользователя
      await fetchItems(true);

      // Обновляем данные пользователя с бэка
      await fetchUser();

      // Воспроизводим звук продажи
      if (isSoundEnabled) {
        const audio = new Audio(sellSound);
        audio.play();
      }

      toast.success(
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src={weapon.iconUrl}
            alt={weapon.name}
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.3))'
            }}
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>Предмет продан</div>
            <div style={{ fontSize: '12px', opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
              {weapon.name}
              <span>за</span>
              <PriceInline>
                <CurrencyIcon size={14} />
                {weapon.tokenPrice?.formatted || weapon.tokenPrice}
              </PriceInline>
            </div>
          </div>
        </div>,
        {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        }
      );

      // Небольшая задержка для обновления стора перед переходом назад
      await new Promise(resolve => setTimeout(resolve, 100));

      // Возвращаемся назад
      navigate(-1);
    } catch (error) {
      console.error('Error selling weapon:', error);
      toast.error('Произошла ошибка при продаже предмета', {
        position: "top-center",
        theme: "dark",
        style: {
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }
      });
    } finally {
      setIsSelling(false);
      setShowConfirmDialog(false);
    }
  };

  const handleWithdraw = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
    }

    // Проверяем, что weapon загружен
    if (!weapon) {
      console.error('SkinDetails - weapon is not loaded yet');
      toast.error('Предмет еще загружается, попробуйте позже');
      return;
    }

    // Добавляем отладочную информацию
    console.log('SkinDetails - handleWithdraw called');
    console.log('SkinDetails - weapon before navigation:', weapon);
    console.log('SkinDetails - weapon properties:', weapon ? {
      id: weapon.id,
      name: weapon.name,
      marketHashName: weapon.marketHashName,
      market_hash_name: weapon.market_hash_name,
      iconUrl: weapon.iconUrl,
      tokenPrice: weapon.tokenPrice
    } : 'weapon is null/undefined');

    navigate('/withdraw', { state: { weapon } });
  };

  const handleShare = async () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
    }
    if (!weapon) return;
    setShareLoading(true);
    try {
      const response = await apiService.savePreparedInlineMessage({ item_id: weapon.id });
      const inlineMessageId = response.id;
      console.log('inlineMessageId', inlineMessageId);
      if (window.Telegram?.WebApp?.openTelegramLink && inlineMessageId) {
        window.Telegram.WebApp.shareMessage(inlineMessageId);
      } else {
        toast.success('id скопирован: ' + inlineMessageId);
        await navigator.clipboard.writeText(inlineMessageId);
      }
    } catch (e) {
      toast.error('Ошибка при подготовке сообщения');
    } finally {
      setShareLoading(false);
    }
  };

  const handleFarmClick = () => {
    navigate('/farm');
  };

  // Используем либо weapon из стора, либо apiWeapon из API
  const currentWeapon = weapon || apiWeapon;
  const { name, description } = parseMarketHashName(currentWeapon?.marketHashName);

  const renderContent = () => {
    console.log('Рендер контента:', { loading, error, weapon });

    if (loading) {
      return (
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24 }}>
            <div style={{ width: 220, height: 180, background: 'rgba(200,200,200,0.12)', borderRadius: 16, marginBottom: 16 }} />
            <div style={{ width: 120, height: 24, background: 'rgba(200,200,200,0.12)', borderRadius: 8, marginBottom: 8 }} />
            <div style={{ width: 80, height: 18, background: 'rgba(200,200,200,0.12)', borderRadius: 8, marginBottom: 16 }} />
            <div style={{ width: 100, height: 18, background: 'rgba(200,200,200,0.12)', borderRadius: 8, marginBottom: 16 }} />
            <div style={{ width: 180, height: 32, background: 'rgba(200,200,200,0.12)', borderRadius: 12 }} />
          </div>
        </div>
      );
    }

    if (error || !currentWeapon) {
      return <ErrorText>{error || t('skin.notFound')}</ErrorText>;
    }

    return (
      <>
        <div style={{ background: 'linear-gradient(90deg, #23272f 0%, #181c24 100%)' }}>
          <ImageSection>
            <WeaponImage src={currentWeapon.iconUrl} alt={name} />
          </ImageSection>

          <WeaponTitle>
            <h1>{name}</h1>
            <p>{description}</p>
          </WeaponTitle>

          <ActionButtons>
            <ActionButton onClick={handleSellClick} disabled={isSelling || loading || currentWeapon.status === 'pending' || currentWeapon.status === 'sent' || currentWeapon.status === 'hold'}>
              <CurrencyIcon size={20} />
              <ActionText>{t('skin.sell')}</ActionText>
            </ActionButton>
            <ActionButton onClick={handleWithdraw} disabled={isSelling || loading || currentWeapon.status === 'pending' || currentWeapon.status === 'sent' || currentWeapon.status === 'hold'}>
              <FaSteam />
              <ActionText>{t('skin.withdraw')}</ActionText>
            </ActionButton>
            <ActionButton onClick={handleShare} disabled={isSelling || loading}>
              <IoShareSocial />
              <ActionText>{t('skin.share')}</ActionText>
            </ActionButton>
          </ActionButtons>
        </div>
        <div style={{ padding: '20px 20px 80px 20px' }}>
          <InfoTable>
            <InfoRow>
              <InfoLabel>{t('skin.owner')}</InfoLabel>
              <InfoValue>{typeof currentWeapon.owner === 'object' && currentWeapon.owner !== null ? currentWeapon.owner.name : currentWeapon.owner || '—'}</InfoValue>
            </InfoRow>
            {/* <InfoRow>
              <InfoLabel>{t('skin.condition')}</InfoLabel>
              <InfoValue>{currentWeapon.condition || t('skin.notSpecified')}</InfoValue>
            </InfoRow> */}
            <InfoRow>
              <InfoLabel>{t('skin.price')}</InfoLabel>
              <InfoValue>
                {currentWeapon.tokenPrice ? (
                  <PriceInline>
                    <CurrencyIcon size={14} />
                    {currentWeapon.tokenPrice}
                  </PriceInline>
                ) : '—'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('skin.status')}</InfoLabel>
              <InfoValue style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: currentWeapon.status === 'error' ? '#ff4444' : undefined, fontWeight: currentWeapon.status === 'error' ? 600 : undefined }}>
                    {currentWeapon.status === 'hold' ? t('skins.statusHold') : currentWeapon.status ? capitalize(currentWeapon.status) : '—'}
                  </span>
                  {currentWeapon.status === 'error' && (
                    <span
                      style={{ cursor: 'pointer', color: '#007AFF', fontSize: 18, marginLeft: 6, display: 'flex', alignItems: 'center' }}
                      title={t('tradeErrorHelp.title')}
                      onClick={() => setShowTradeHelp(true)}
                    >
                      ℹ️
                    </span>
                  )}
                </span>
              </InfoValue>
            </InfoRow>
            {((currentWeapon.releaseDate && timeLeft) || (currentWeapon.status === 'hold')) && (
              <InfoRow>
                <InfoLabel style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{t('skins.awaitingUnlock')}</span>
                </InfoLabel>
                <InfoValue style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ffa500' }}>
                  <span style={{ fontSize: 16 }}>❄️</span>
                  <span style={{
                    background: '#ffa500',
                    color: '#000',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    {timeLeft}
                  </span>
                </InfoValue>
              </InfoRow>
            )}
          </InfoTable>
        </div>

        {showConfirmDialog && currentWeapon && (
          <ConfirmDialog>
            <DialogOverlay onClick={() => setShowConfirmDialog(false)} />
            <DialogContent>
              <DialogHeader>
                <ModalWeaponImage
                  src={currentWeapon.iconUrl}
                  alt={name}
                />
                <DialogTitle>{t('skin.sellConfirmTitle')}</DialogTitle>
              </DialogHeader>
              <DialogText as="div" style={{ fontSize: 17, color: '#bbb', marginBottom: 32 }}>
                <Trans
                  i18nKey="skin.sellConfirmText"
                  values={{ name, price: currentWeapon.tokenPrice }}
                  components={{
                    b: <b style={{ fontWeight: 700, color: primaryTextColor }} />,
                    span: <PriceInline className="price" />,
                    currency: <CurrencyIcon size={16} />
                  }}
                />
              </DialogText>
              <DialogActions style={{ justifyContent: 'center', gap: 20 }}>
                <CancelButton onClick={() => setShowConfirmDialog(false)}>
                  {t('skin.cancel')}
                </CancelButton>
                <ConfirmButton onClick={handleSell}>
                  {t('skin.sell')}
                </ConfirmButton>
              </DialogActions>
            </DialogContent>
          </ConfirmDialog>
        )}

        {showTradeHelp && (
          <div style={{
            position: 'fixed', left: 0, top: 0, right: 0, bottom: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: theme.card.background,
              borderRadius: 20,
              padding: 0,
              maxWidth: 440,
              width: '92vw',
              color: theme.text.primary,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              overflow: 'hidden',
              paddingBottom: 32
            }}>
              <TradeErrorHelp />
              <CloseHelpButton onClick={() => setShowTradeHelp(false)}>
                {t('case.close')}
              </CloseHelpButton>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Container>
      {renderContent()}
      {currentWeapon && !loading && !error && currentWeapon.status !== 'pending' && currentWeapon.status !== 'sent' && currentWeapon.status !== 'hold' && currentWeapon.status !== 'sold' && (
        <BottomContainer>
          <SellButton onClick={handleSellClick} disabled={isSelling}>
            {isSelling ? (
              <>
                <Spinner />
                <span style={{ marginLeft: 10 }}>{t('profile.selling') || 'Продаём...'}</span>
              </>
            ) : (
              <>
                {t('skin.sell') || 'Продать'}
                <PriceInline style={{ marginLeft: 8 }}>
                  <CurrencyIcon size={16} />
                  {currentWeapon?.tokenPrice?.formatted || currentWeapon?.tokenPrice}
                </PriceInline>
              </>
            )}
          </SellButton>
        </BottomContainer>
      )}


    </Container>
  );
};

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.text?.secondary || '#aaa'};
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error || '#ff4444'};
`;

export default SkinDetails; 